import { useCallback, useEffect, useRef } from "react";

import { usePreviewContext } from "../contexts/preview-context";
import { useKeyDown, useKeyUp } from "./useKeyboard";
import { useCanMoveZone, useZoneMove } from "../components/slideshow-preview/hooks/use-zone-move";
import { useZoneResize } from "../components/slideshow-preview/hooks/use-zone-resize";

const ARROW_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"] as const;
type ArrowKey = (typeof ARROW_KEYS)[number];

function isArrowKey(key: string): key is ArrowKey {
	return (ARROW_KEYS as readonly string[]).includes(key);
}

interface UseArrowKeyZoneMoveOptions {
	zone: eXpress.core.IZone | undefined;
	isEnabled?: boolean;
}

/**
 * Moves the selected zone using arrow keys.
 *
 * - Single press: moves 1 visual screen pixel.
 * - Hold: accelerates movement up to 8 visual screen pixels per step.
 * - Key release: finalizes the undo command block (non-interactive).
 */
export function useArrowKeyZoneMove({ zone, isEnabled = true }: UseArrowKeyZoneMoveOptions) {
	const [isZoneMoving, zoneMoving] = useZoneMove();
	const [isZoneResizing] = useZoneResize();
	const { getScreenToSpreadTransform } = usePreviewContext();
	const canMoveZone = useCanMoveZone({ zone, isEnabled, isZoneResizing });

	/** Track the active arrow-key combination to mimic the desktop behavior. */
	const activeKeysRef = useRef<Set<ArrowKey>>(new Set());
	const movingZoneRef = useRef<eXpress.core.ITransformableZone | undefined>(undefined);
	const offsetRef = useRef({ x: 0, y: 0 });
	const lastKeyCombinationRef = useRef("");
	const lastKeyCountRef = useRef(0);

	const resetMoveState = useCallback(() => {
		activeKeysRef.current.clear();
		offsetRef.current = { x: 0, y: 0 };
		lastKeyCombinationRef.current = "";
		lastKeyCountRef.current = 0;
		movingZoneRef.current = undefined;
	}, []);

	const endMove = useCallback(() => {
		if (movingZoneRef.current) {
			zoneMoving.end();
		}
		resetMoveState();
	}, [resetMoveState, zoneMoving]);

	const getDipPerScreenPixel = useCallback(() => {
		if (!zone) return 1;
		return getScreenToSpreadTransform(zone.spread, undefined)?.scale ?? 1;
	}, [zone, getScreenToSpreadTransform]);

	useEffect(() => {
		if (!isEnabled) {
			endMove();
			return;
		}

		if (movingZoneRef.current && zone !== movingZoneRef.current) {
			endMove();
		}
	}, [endMove, isEnabled, zone]);

	useKeyDown(
		useCallback(
			(event: KeyboardEvent) => {
				const { key } = event;
				if (!isArrowKey(key)) return;

				if (!canMoveZone) {
					endMove();
					return;
				}

				// If a pointer-driven zone move is active, don't start a keyboard move.
				if (!movingZoneRef.current && isZoneMoving) return;

				event.preventDefault();
				event.stopPropagation();

				const transformableZone = zone as eXpress.core.ITransformableZone;

				if (movingZoneRef.current && movingZoneRef.current !== transformableZone) {
					endMove();
					return;
				}

				if (!movingZoneRef.current) {
					zoneMoving.startFromCurrentTransform(transformableZone);
					movingZoneRef.current = transformableZone;
				}

				activeKeysRef.current.add(key);
				const keyCombination = ARROW_KEYS.filter((arrowKey) =>
					activeKeysRef.current.has(arrowKey),
				).join("+");

				if (lastKeyCombinationRef.current !== keyCombination) {
					lastKeyCombinationRef.current = keyCombination;
					lastKeyCountRef.current = 0;
				}

				lastKeyCountRef.current += 1;

				// Calculate movement in screen pixels (matches desktop behavior):
				// - Single press: 1 screen pixel
				// - Hold: accelerates from 0.5 to a maximum of 8 screen pixels per step
				let offsetInScreenPixels: number;
				if (!event.repeat) {
					offsetInScreenPixels = 1;
				} else {
					const step = 0.5 + (lastKeyCountRef.current * lastKeyCountRef.current) / 1000;
					offsetInScreenPixels = Math.max(Math.min(step, 8), 0.5);
				}

				const dipPerScreenPixel = getDipPerScreenPixel();
				const offset = offsetInScreenPixels * dipPerScreenPixel;

				if (key === "ArrowLeft") offsetRef.current.x -= offset;
				else if (key === "ArrowRight") offsetRef.current.x += offset;
				else if (key === "ArrowUp") offsetRef.current.y -= offset;
				else if (key === "ArrowDown") offsetRef.current.y += offset;

				zoneMoving.updateWithDisplacement(
					eXpress.math.vector(offsetRef.current.x, offsetRef.current.y),
					{ shouldSnap: false },
				);
			},
			[zone, canMoveZone, isZoneMoving, endMove, getDipPerScreenPixel, zoneMoving],
		),
	);

	useKeyUp(
		useCallback(
			(event: KeyboardEvent) => {
				if (!isArrowKey(event.key)) return;

				endMove();
			},
			[endMove],
		),
	);

	useEffect(() => {
		const onWindowBlur = () => {
			endMove();
		};

		window.addEventListener("blur", onWindowBlur);
		return () => {
			window.removeEventListener("blur", onWindowBlur);
		};
	}, [endMove]);
}
